/**
 * Stacks overlapping Shopify app launchers (accessibility, dark mode, etc.).
 * Handles: (1) one fixed wrapper with multiple children, (2) open shadow roots,
 * (3) separate fixed nodes, (4) small fixed iframes in the same corner.
 */
(function () {
  /** Compact launchers- low visual priority but still tappable */
  const TARGET_PX = 34;
  const GAP_PX = 6;
  const MAX_FROM_LEFT = 220;
  const MAX_FROM_BOTTOM = 320;
  const MIN_HIT = 24;
  const MAX_HIT = 200;

  const DENY_SELECTORS = [
    '.mobile-home-nav__dropdown',
    '.mobile-home-nav__backdrop',
    '#search-modal',
    'dialog',
    '[role="dialog"]',
    '.menu-drawer',
    '.quick-add-modal',
    '#google-merchantwidget-iframe-wrapper',
  ].join(', ');

  function deniedHost(el) {
    return !el?.closest || el.closest(DENY_SELECTORS);
  }

  function inBottomLeftZone(rect) {
    const fromBottom = window.innerHeight - rect.bottom;
    return rect.left <= MAX_FROM_LEFT && fromBottom <= MAX_FROM_BOTTOM;
  }

  /** Depth-first walk including open shadow roots. */
  function walkElements(root, visitor) {
    if (!root) return;
    if (root.nodeType === 1) visitor(/** @type {Element} */ (root));
    const children = root.children;
    if (children) {
      for (let i = 0; i < children.length; i++) walkElements(children[i], visitor);
    }
    if (root instanceof HTMLElement && root.shadowRoot) {
      walkElements(root.shadowRoot, visitor);
    }
  }

  function ancestorsComposed(/** @type {Node | null} */ node) {
    /** @type {Node[]} */
    const chain = [];
    let el = node;
    while (el) {
      chain.unshift(el);
      const p = el.parentNode;
      if (!p) break;
      el = p instanceof ShadowRoot ? p.host : p;
    }
    return chain;
  }

  /** True if el is visible and looks like a launcher control. */
  function isVisibleLauncher(el) {
    if (!(el instanceof HTMLElement) || deniedHost(el)) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (parseFloat(cs.opacity) < 0.08) return false;
    const r = el.getBoundingClientRect();
    if (r.width < MIN_HIT || r.height < MIN_HIT || r.width > MAX_HIT || r.height > MAX_HIT) return false;
    return inBottomLeftZone(r);
  }

  /** Collect buttons / links / focusable tiles under root (light + shadow). */
  function collectLaunchersUnder(root) {
    /** @type {HTMLElement[]} */
    const found = [];
    const tag = (el) => {
      if (
        el.matches(
          'button, a[href], [role="button"], input[type="button"], input[type="submit"], [tabindex]:not([tabindex="-1"])'
        )
      ) {
        return true;
      }
      /* Icon-only launchers (many apps use a div + svg, not <button>) */
      if (
        el.tagName === 'DIV' &&
        el.querySelector(':scope > svg') &&
        el.childElementCount <= 4
      ) {
        return true;
      }
      return false;
    };

    walkElements(root, (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (!tag(el)) return;
      if (!isVisibleLauncher(el)) return;
      found.push(el);
    });
    return found;
  }

  function lowestCommonAncestor(/** @type {HTMLElement[]} */ nodes) {
    if (nodes.length === 0) return null;
    const chains = nodes.map((n) => ancestorsComposed(n));
    let i = 0;
    let lca = null;
    for (;;) {
      const cur = chains[0][i];
      if (!cur || !chains.every((c) => c[i] === cur)) break;
      lca = cur;
      i++;
    }
    return lca instanceof HTMLElement ? lca : null;
  }

  function centersClose(a, b, px) {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    const cxa = ra.left + ra.width / 2;
    const cya = ra.top + ra.height / 2;
    const cxb = rb.left + rb.width / 2;
    const cyb = rb.top + rb.height / 2;
    return Math.hypot(cxa - cxb, cya - cyb) < px;
  }

  /** Group launchers that overlap or sit on the same pile (union-find). */
  function clusterLaunchers(/** @type {HTMLElement[]} */ items) {
    if (items.length < 2) return [];
    const n = items.length;
    const parent = Array.from({ length: n }, (_, i) => i);
    function find(i) {
      return parent[i] === i ? i : (parent[i] = find(parent[i]));
    }
    function union(a, b) {
      parent[find(a)] = find(b);
    }
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const ri = items[i].getBoundingClientRect();
        const rj = items[j].getBoundingClientRect();
        const ix = Math.max(0, Math.min(ri.right, rj.right) - Math.max(ri.left, rj.left));
        const iy = Math.max(0, Math.min(ri.bottom, rj.bottom) - Math.max(ri.top, rj.top));
        const overlap = ix >= 3 && iy >= 3;
        if (overlap || centersClose(items[i], items[j], 88)) union(i, j);
      }
    }
    const map = new Map();
    for (let i = 0; i < n; i++) {
      const r = find(i);
      if (!map.has(r)) map.set(r, []);
      map.get(r).push(items[i]);
    }
    return [...map.values()].filter((g) => g.length >= 2);
  }

  function normalizeLauncher(el) {
    el.style.setProperty('width', `${TARGET_PX}px`, 'important');
    el.style.setProperty('height', `${TARGET_PX}px`, 'important');
    el.style.setProperty('min-width', `${TARGET_PX}px`, 'important');
    el.style.setProperty('min-height', `${TARGET_PX}px`, 'important');
    el.style.setProperty('max-width', `${TARGET_PX}px`, 'important');
    el.style.setProperty('max-height', `${TARGET_PX}px`, 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
    el.style.setProperty('border-radius', '9999px', 'important');
    el.style.setProperty('flex-shrink', '0', 'important');
    el.style.setProperty('margin', '0', 'important');
  }

  function flexStackParent(parent, /** @type {HTMLElement[]} */ children) {
    if (!(parent instanceof HTMLElement)) return;
    parent.style.setProperty('display', 'flex', 'important');
    parent.style.setProperty('flex-direction', 'column', 'important');
    parent.style.setProperty('align-items', 'flex-start', 'important');
    parent.style.setProperty('justify-content', 'flex-end', 'important');
    parent.style.setProperty('gap', `${GAP_PX}px`, 'important');
    parent.style.setProperty('width', 'auto', 'important');
    parent.style.setProperty('height', 'auto', 'important');
    parent.style.setProperty('min-width', '0', 'important');
    parent.style.setProperty('background', 'transparent', 'important');
    parent.style.setProperty('box-shadow', 'none', 'important');
    parent.dataset.stackAppWidgets = 'flex';
    children.forEach(normalizeLauncher);
  }

  /** Strategy A: shopify app block roots- search shadow + light DOM for clustered controls. */
  function stackFromAppBlocks() {
    const roots = document.querySelectorAll('[id^="shopify-block-"], .shopify-app-block');
    for (const root of roots) {
      if (!(root instanceof HTMLElement) || deniedHost(root)) continue;
      const launchers = collectLaunchersUnder(root);
      const groups = clusterLaunchers(launchers);
      for (const group of groups) {
        group.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
        const lca = lowestCommonAncestor(group);
        if (lca) flexStackParent(lca, group);
      }
    }
  }

  /** Strategy B: fixed wrapper with 2+ circle-like children (absolute/fixed inside). */
  function stackFixedWrappers() {
    walkElements(document.body, (el) => {
      if (!(el instanceof HTMLElement) || deniedHost(el)) return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed') return;
      const r = el.getBoundingClientRect();
      if (!inBottomLeftZone(r)) return;
      if (r.width > 360 || r.height > 360) return;

      const kids = [...el.children].filter((c) => c instanceof HTMLElement && isVisibleLauncher(c));
      if (kids.length >= 2) {
        kids.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
        flexStackParent(el, kids);
      }
    });
  }

  /** Strategy C: multiple separate fixed nodes (original behaviour). */
  function stackSeparateFixedNodes() {
    /** @type {HTMLElement[]} */
    const out = [];
    walkElements(document.body, (el) => {
      if (!(el instanceof HTMLElement) || deniedHost(el)) return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed') return;
      const r = el.getBoundingClientRect();
      if (r.width < MIN_HIT || r.height < MIN_HIT || r.width > MAX_HIT || r.height > MAX_HIT) return;
      if (!inBottomLeftZone(r)) return;
      out.push(el);
    });

    const candidates = out.filter(
      (el) => !out.some((other) => other !== el && el.contains(other))
    );

    function clusterIntoGroups(els) {
      if (els.length < 2) return [];
      const rects = els.map((e) => e.getBoundingClientRect());
      const n = els.length;
      const parent = Array.from({ length: n }, (_, i) => i);
      function find(i) {
        return parent[i] === i ? i : (parent[i] = find(parent[i]));
      }
      function union(a, b) {
        parent[find(a)] = find(b);
      }
      function overlaps(i, j) {
        const ri = rects[i];
        const rj = rects[j];
        const ix = Math.max(0, Math.min(ri.right, rj.right) - Math.max(ri.left, rj.left));
        const iy = Math.max(0, Math.min(ri.bottom, rj.bottom) - Math.max(ri.top, rj.top));
        if (ix >= 4 && iy >= 4) return true;
        const cxi = ri.left + ri.width / 2;
        const cyi = ri.top + ri.height / 2;
        const cxj = rj.left + rj.width / 2;
        const cyj = rj.top + rj.height / 2;
        return Math.hypot(cxi - cxj, cyi - cyj) < 72;
      }
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (overlaps(i, j)) union(i, j);
        }
      }
      const map = new Map();
      for (let i = 0; i < n; i++) {
        const root = find(i);
        if (!map.has(root)) map.set(root, []);
        map.get(root).push(els[i]);
      }
      return [...map.values()].filter((g) => g.length >= 2);
    }

    let groups = clusterIntoGroups(candidates);
    if (groups.length === 0 && candidates.length === 2) groups = [candidates];

    const step = TARGET_PX + GAP_PX;
    for (const els of groups) {
      els.sort((a, b) => {
        const pos = a.compareDocumentPosition(b);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      const leftAlign = Math.max(12, Math.min(...els.map((e) => e.getBoundingClientRect().left)));
      const bottoms = els.map((e) => parseFloat(getComputedStyle(e).bottom)).filter(Number.isFinite);
      const baseBottom = bottoms.length ? Math.min(...bottoms) : 20;
      els.forEach((el, i) => {
        el.style.setProperty('left', `${Math.round(leftAlign)}px`, 'important');
        el.style.setProperty('right', 'auto', 'important');
        el.style.setProperty('bottom', `${Math.round(baseBottom + i * step)}px`, 'important');
        el.style.setProperty('top', 'auto', 'important');
        normalizeLauncher(el);
        el.style.setProperty('display', 'flex', 'important');
        el.style.setProperty('align-items', 'center', 'important');
        el.style.setProperty('justify-content', 'center', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
        el.dataset.stackAppWidgets = 'fixed';
      });
    }
  }

  /** Strategy D: two small fixed iframes in the corner. */
  function stackIframes() {
    const frames = [...document.querySelectorAll('body iframe')].filter((f) => {
      if (!(f instanceof HTMLIFrameElement)) return false;
      const cs = getComputedStyle(f);
      if (cs.position !== 'fixed') return false;
      const r = f.getBoundingClientRect();
      if (r.width < MIN_HIT || r.height < MIN_HIT || r.width > MAX_HIT + 40) return false;
      return inBottomLeftZone(r);
    });
    if (frames.length < 2) return;
    frames.sort((a, b) => {
      const pos = a.compareDocumentPosition(b);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    const leftAlign = Math.max(12, Math.min(...frames.map((f) => f.getBoundingClientRect().left)));
    const bottoms = frames.map((f) => parseFloat(getComputedStyle(f).bottom)).filter(Number.isFinite);
    const baseBottom = bottoms.length ? Math.min(...bottoms) : 20;
    const step = TARGET_PX + GAP_PX;
    frames.forEach((f, i) => {
      f.style.setProperty('left', `${Math.round(leftAlign)}px`, 'important');
      f.style.setProperty('right', 'auto', 'important');
      f.style.setProperty('bottom', `${Math.round(baseBottom + i * step)}px`, 'important');
      f.style.setProperty('width', `${TARGET_PX}px`, 'important');
      f.style.setProperty('height', `${TARGET_PX}px`, 'important');
      f.style.setProperty('border-radius', '9999px', 'important');
      f.dataset.stackAppWidgets = 'iframe';
    });
  }

  function run() {
    try {
      stackFromAppBlocks();
      stackFixedWrappers();
      stackSeparateFixedNodes();
      stackIframes();
    } catch (_) {
      /* ignore */
    }
  }

  let debounceTimer;
  function debouncedRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(run, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', () => {
    setTimeout(run, 100);
    setTimeout(run, 600);
    setTimeout(run, 1800);
    setTimeout(run, 4000);
  });

  const obs = new MutationObserver(debouncedRun);
  obs.observe(document.body, { childList: true, subtree: true });
})();
